import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { getActiveRoomCode, clearActiveRoomCode } from '../utils/activeRoomCode';

const DAY_MS = 24 * 60 * 60 * 1000;

export default function DashboardPage() {
  const { teacher, isLightweightAccount, isSubscriber, fetchMe } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ctaHovered, setCtaHovered] = useState(false);
  const [upgradeHovered, setUpgradeHovered] = useState(false);

  // Captured once so the banner stays after the param is stripped below.
  const [showSubscribedBanner] = useState(() => searchParams.get('subscribed') === 'true');

  // Refresh teacher data once on mount when returning from Stripe checkout,
  // so the UI reflects the subscription status after the webhook fires.
  // Strip the query param so it doesn't persist on back-navigation. Spec §9.
  const didRefresh = useRef(false);
  useEffect(() => {
    if (showSubscribedBanner && !didRefresh.current) {
      didRefresh.current = true;
      fetchMe();
      setSearchParams({}, { replace: true });
    }
  }, [showSubscribedBanner, fetchMe, setSearchParams]);

  const [activeRoomCode, setActiveRoomCode] = useState(() => getActiveRoomCode());

  // 'cancelled' means it won't renew; access continues until the expiry date.
  const subEndsAt = teacher?.subscription_expires_at ? new Date(teacher.subscription_expires_at) : null;
  const subDate = subEndsAt
    ? subEndsAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';
  const willRenew = teacher?.subscription_status !== 'cancelled';
  const daysLeft = subEndsAt ? Math.ceil((subEndsAt - Date.now()) / DAY_MS) : null;
  const showEndingNotice = !willRenew && daysLeft !== null && daysLeft <= 30;

  function handleRejoin() {
    navigate(`/host/${activeRoomCode}`);
  }

  function handleDismissRejoin() {
    clearActiveRoomCode();
    setActiveRoomCode(null);
  }

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <h1 style={styles.heading}>Dashboard</h1>
        <p style={styles.sub}>
          Welcome{teacher?.classroom_name ? `, ${teacher.classroom_name}` : ''}
        </p>
      </div>

      {/* Payment confirmation banner — shown after returning from Stripe checkout.
          Subscription activation is handled by the webhook, not this redirect. */}
      {showSubscribedBanner && (
        <div style={styles.subscribedBanner}>
          <strong>Payment received!</strong> Your subscription is activating — this usually takes a few seconds.
          Refresh the page if your full library access hasn't appeared yet.
        </div>
      )}

      {/* Active game rejoin banner */}
      {activeRoomCode && (
        <div style={styles.rejoinCard}>
          <div style={styles.rejoinContent}>
            <span style={styles.rejoinDot} />
            <div>
              <div style={styles.rejoinHeading}>Game in progress — Room {activeRoomCode}</div>
              <div style={styles.rejoinSub}>You have an active game session. Rejoin to continue hosting.</div>
            </div>
          </div>
          <div style={styles.rejoinActions}>
            <button onClick={handleRejoin} style={styles.rejoinBtn}>Rejoin game →</button>
            <button onClick={handleDismissRejoin} style={styles.dismissBtn}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Primary CTA */}
      <div style={styles.ctaCard}>
        <div style={styles.ctaContent}>
          <h2 style={styles.ctaHeading}>Ready to play?</h2>
          <p style={styles.ctaText}>
            Set up a game in under two minutes. Students join from any device — no app needed.
          </p>
        </div>
        <Link
          to="/game/new"
          onMouseEnter={() => setCtaHovered(true)}
          onMouseLeave={() => setCtaHovered(false)}
          style={{ ...styles.ctaButton, ...(ctaHovered ? styles.ctaButtonHover : {}) }}
        >
          Create a new game →
        </Link>
      </div>

      {/* TpT unlocked novels */}
      {teacher?.tpt_novels?.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>Your unlocked novels</h2>
          <div style={styles.novelGrid}>
            {teacher.tpt_novels.map((novel) => (
              <div key={novel.novel_id} style={styles.novelCard}>
                <div style={styles.novelTitle}>{novel.title}</div>
                <div style={styles.novelBadge}>Free forever</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscriber full library access */}
      {isSubscriber && (
        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>Your subscription</h2>
          <div style={styles.subCard}>
            <span style={styles.subBadge}>Active</span>
            <span style={styles.subText}>
              Full library access · {willRenew ? 'renews' : 'ends'} {subDate}
            </span>
          </div>
          {showEndingNotice && (
            <p style={styles.endingNotice}>
              Your full access ends {subDate} ({daysLeft} day{daysLeft === 1 ? '' : 's'}).{' '}
              <Link to="/account" style={styles.endingLink}>Keep subscription →</Link>
            </p>
          )}
        </div>
      )}

      {/* Upgrade prompts */}
      {!isSubscriber && (
        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>Want the full library?</h2>
          <div style={styles.upgradeCard}>
            <p style={styles.upgradeText}>
              Subscribe for $19.99/year to unlock all novels, custom vocabulary uploads, and saved lists.
              Your TpT novel{teacher?.tpt_novels?.length > 1 ? 's remain' : ' remains'} free forever either way.
            </p>
            <Link
              to={isLightweightAccount ? '/signup?upgrade=true' : '/subscribe'}
              onMouseEnter={() => setUpgradeHovered(true)}
              onMouseLeave={() => setUpgradeHovered(false)}
              style={{ ...styles.upgradeButton, ...(upgradeHovered ? styles.upgradeButtonHover : {}) }}
            >
              {isLightweightAccount ? 'Create a full account first →' : 'Subscribe for $19.99/yr →'}
            </Link>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

const styles = {
  subscribedBanner: {
    backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '10px',
    padding: '14px 20px', marginBottom: '20px',
    fontSize: '14px', color: '#15803d', lineHeight: '1.5',
  },
  rejoinCard: {
    backgroundColor: '#fefce8', border: '2px solid #fcd34d', borderRadius: '12px',
    padding: '20px 24px', marginBottom: '24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '16px', flexWrap: 'wrap',
  },
  rejoinContent: { display: 'flex', alignItems: 'center', gap: '14px', flex: 1 },
  rejoinDot: {
    width: '10px', height: '10px', borderRadius: '50%',
    backgroundColor: '#16a34a', flexShrink: 0,
    boxShadow: '0 0 0 3px rgba(22,163,74,0.25)',
  },
  rejoinHeading: { fontSize: '15px', fontWeight: '700', color: 'var(--color-text)' },
  rejoinSub: { fontSize: '13px', color: '#6b6b7a', marginTop: '2px' },
  rejoinActions: { display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 },
  rejoinBtn: {
    padding: '10px 20px', backgroundColor: 'var(--color-primary)', color: '#fff',
    border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
  },
  dismissBtn: {
    background: 'none', border: 'none', color: '#9ca3af',
    fontSize: '13px', cursor: 'pointer', padding: '4px',
  },
  header: { marginBottom: '32px' },
  heading: { fontSize: '28px', fontWeight: '700', color: 'var(--color-text)', margin: '0 0 4px' },
  sub: { fontSize: '15px', color: '#6b6b7a', margin: 0 },
  ctaCard: {
    backgroundColor: 'var(--color-primary)', borderRadius: '12px', padding: '32px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '24px', flexWrap: 'wrap', marginBottom: '32px',
  },
  ctaContent: { flex: 1 },
  ctaHeading: { fontSize: '20px', fontWeight: '700', color: '#fff', margin: '0 0 8px' },
  ctaText: { fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0, maxWidth: '400px' },
  ctaButton: {
    padding: '12px 24px', backgroundColor: 'var(--color-accent)', color: 'var(--color-text)',
    borderRadius: '8px', fontWeight: '700', fontSize: '15px',
    textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all 0.15s',
    border: '2px solid #fff',
  },
  ctaButtonHover: { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
  section: { marginBottom: '32px' },
  sectionHeading: { fontSize: '16px', fontWeight: '600', color: 'var(--color-text)', margin: '0 0 16px' },
  novelGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  novelCard: {
    backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px',
  },
  novelTitle: { fontSize: '15px', fontWeight: '500', color: 'var(--color-text)' },
  novelBadge: {
    fontSize: '11px', fontWeight: '600', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '20px',
  },
  subCard: {
    backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px',
  },
  subBadge: {
    fontSize: '11px', fontWeight: '600', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '20px',
  },
  subText: { fontSize: '14px', color: '#374151' },
  endingNotice: { fontSize: '13px', color: '#92400e', margin: '10px 0 0' },
  endingLink: { color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none' },
  upgradeCard: {
    backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '20px 24px',
  },
  upgradeText: { fontSize: '14px', color: '#6b6b7a', margin: '0 0 16px', lineHeight: '1.6' },
  upgradeButton: {
    display: 'inline-block', padding: '10px 20px', backgroundColor: 'var(--color-primary)',
    color: '#fff', borderRadius: '8px', fontWeight: '600', fontSize: '14px',
    textDecoration: 'none', transition: 'all 0.15s',
  },
  upgradeButtonHover: { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
};
